import { Theme, css } from '@emotion/react';
import { Cake, Discount as DiscountIcon, EventNote, School } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useCommunityContext } from 'providers';
import { useMemo } from 'react';

import { DiscountStatus, OrganizationStatus } from '@jebel/constants';

import { ReedemDiscountModal } from 'features/discounts/components/ReedemDiscountModal';
import { useRandomDiscounts } from 'features/discounts/hooks';
import { OrganizationSidebarItem } from 'features/organizations/components';
import { useRandomSchoolSupporters } from 'features/organizations/hooks';
import { PaperLayout } from 'shared/components/layouts';
import { DiscountPresentationContent, FeedSkeleton, Typography } from 'shared/components/ui';
import { ROUTES, BIRTHDAYS, DISCOUNTS, EVENTS, SUPPORTERS } from 'shared/constants';
import { DiscountListItemFragment as Discount, UserListItemFragment } from 'shared/graphql';
import { useModalState } from 'shared/hooks';
import { getUsersByBirthday } from 'shared/utils/user';

import { EventCard, UserCard } from './components';
import { ModuleContainer } from './components/ModuleContainer';
import { useMemberCommunitySidebarItems } from './hooks';

const containerCSS = css`
  width: 100%;
`;

const emptyContainer = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const viewAllLinkCSS = (theme: Theme) => css`
  color: ${theme.palette.secondary.main};
`;

interface CommunitySidebarProps {
  items?: string[] | null | undefined;
  isMainContentLoading?: boolean;
}

export function CommunitySidebar({ items, isMainContentLoading = false }: CommunitySidebarProps) {
  const { selectedCommunity: school } = useCommunityContext();

  const {
    show: isDiscountModalOpen,
    params: selectedDiscount,
    open: onDiscountModalOpen,
    close: onDiscountModalClose,
  } = useModalState<Discount>();

  const allowedSections = useMemo(() => {
    return items ?? [SUPPORTERS, BIRTHDAYS, DISCOUNTS, EVENTS];
  }, [items]);

  const showSupporters = useMemo(() => allowedSections.includes(SUPPORTERS), [allowedSections]);
  const showDiscounts = useMemo(() => allowedSections.includes(DISCOUNTS), [allowedSections]);
  const showBirthdays = useMemo(() => allowedSections.includes(BIRTHDAYS), [allowedSections]);
  const showEvents = useMemo(() => allowedSections.includes(EVENTS), [allowedSections]);

  const {
    events,
    birthdays,
    loading: queryLoading,
  } = useMemberCommunitySidebarItems({
    variables: { showBirthdays, showEvents },
  });

  const { data: supporters, loading: supportersLoading } = useRandomSchoolSupporters({
    variables: {
      first: 2,
      filter: {
        status: { equals: OrganizationStatus.Active },
      },
    },
  });

  const { data: discounts, loading: discountsLoading } = useRandomDiscounts({
    variables: {
      first: 3,
      filter: {
        status: { equals: DiscountStatus.Active },
        // https://github.com/jebelapp/jebel/issues/1567
        schools: { some: { id: { equals: school?.id } } },
      },
    },
  });

  const sortedUsersByBirthday = useMemo(
    () => getUsersByBirthday<UserListItemFragment>(birthdays),
    [birthdays],
  );

  const loading = queryLoading || supportersLoading || discountsLoading;

  if (loading || isMainContentLoading) {
    return <FeedSkeleton count={2} />;
  }

  return (
    <PaperLayout css={containerCSS}>
      {selectedDiscount && (
        <ReedemDiscountModal
          isOpen={isDiscountModalOpen}
          onClose={onDiscountModalClose}
          discount={selectedDiscount}
        />
      )}

      {showSupporters && (
        <ModuleContainer
          title="Supporters"
          toViewAll={ROUTES.user.organizations.index}
          icon={<School fontSize="inherit" />}
        >
          {supporters.map(supporter => (
            <OrganizationSidebarItem key={supporter.id} organization={supporter} />
          ))}

          {supporters.length === 0 && (
            <Box css={emptyContainer}>
              <Typography variant="caption" align="center" color="textSecondary">
                No supporters found
              </Typography>
            </Box>
          )}
        </ModuleContainer>
      )}

      {showDiscounts && (
        <ModuleContainer title="Discounts" icon={<DiscountIcon fontSize="inherit" />}>
          {discounts.map(discount => (
            <DiscountPresentationContent
              key={discount?.id}
              onRedeem={() => onDiscountModalOpen(discount)}
              discount={discount}
              withRedeem
            />
          ))}

          {discounts.length === 0 && (
            <Box css={emptyContainer}>
              <Typography variant="caption" align="center" color="textSecondary">
                No active discounts
              </Typography>
            </Box>
          )}
        </ModuleContainer>
      )}

      {showEvents && !!events.length && (
        <ModuleContainer
          title="Upcoming events"
          toViewAll={ROUTES.user.events.index}
          icon={<EventNote fontSize="inherit" />}
        >
          {events.map(event => (
            <EventCard key={event?.id} event={event} />
          ))}

          {events.length === 0 && (
            <Box css={emptyContainer}>
              <Typography variant="caption" align="center" color="textSecondary">
                No upcoming events
              </Typography>
            </Box>
          )}
        </ModuleContainer>
      )}

      {showBirthdays && !!birthdays.length && (
        <ModuleContainer title="Upcoming birthdays" icon={<Cake fontSize="inherit" />}>
          {sortedUsersByBirthday?.map(user => <UserCard key={user?.id} user={user} />)}

          {sortedUsersByBirthday.length === 0 && (
            <Box css={emptyContainer}>
              <Typography variant="caption" align="center" color="textSecondary">
                No upcoming birthdays
              </Typography>
            </Box>
          )}
        </ModuleContainer>
      )}
    </PaperLayout>
  );
}
